<script setup lang="ts">
import type { CustomerAddress } from "@shopware-pwa/types";
import { Heading } from "~/components/clarity-ui/Heading";
import useCommonLabels from "~/composables/useCommonLabels";
import { useModal } from "~/composables/useModal";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import AccountBillingAddressForm from "~/templates/components/shop/account/AccountBillingAddressForm.vue";
import AccountShippingAddressForm from "~/templates/components/shop/account/AccountShippingAddressForm.vue";
import AccountAddressForm from "~/templates/components/shop/account/AccountAddressForm.vue";

export interface AccountAddressCardProps {
  address?: CustomerAddress;
  buttonStyles?: string;
  canEdit?: boolean;
  isBilling?: boolean;
  isShipping?: boolean;
  shippingEditEnabled?: boolean;
  showTitle?: boolean;
}

const {
  buttonStyles = "",
  canEdit = true,
  isBilling,
  isShipping,
  shippingEditEnabled = true,
  showTitle = true,
} = defineProps<AccountAddressCardProps>();

const addressModal = useModal();

const { getLabels } = useCommonLabels();
const { shippingAddressLabel, billingAddressLabel, addressLabel } = getLabels("BWF/shop/shared/", [
  "shippingAddressLabel",
  "billingAddressLabel",
  "addressLabel",
]);

const addressTitle = isShipping
  ? shippingAddressLabel
  : isBilling
    ? billingAddressLabel
    : addressLabel;
</script>

<template>
  <div class="relative flex h-full flex-col gap-4" data-no-tracking>
    <Heading v-if="showTitle" size="small" tag="h2">
      <slot name="title">{{ addressTitle }}</slot>
    </Heading>

    <div v-if="!address?.customFields?.dummyAddress" class="mb-2">
      {{ `${address?.firstName} ${address?.lastName}` }}<br />
      {{ address?.street }}<br />
      <template v-if="address?.additionalAddressLine1"
        >{{ address?.additionalAddressLine1 }}<br
      /></template>
      {{ address?.zipcode }} {{ address?.city }}<br />
      {{ address?.country?.name }}
    </div>

    <template v-if="canEdit">
      <div class="mt-auto xl:place-self-start" @click.prevent="addressModal.open()">
        <slot name="editButton">
          <button
            :class="[
            buttonStyles
              ? buttonStyles
              : 'text-icon-link absolute right-0 top-0 xl:relative xl:right-auto xl:top-auto xl:mt-auto',
          ]"
          >
            <CommonLabel
              v-if="isShipping"
              class="hidden xl:block"
              path="BWF/shop/shared/shippingAddressEdit"
            />
            <CommonLabel v-else class="hidden xl:block" path="BWF/shop/shared/billingAddressEdit" />

            <CommonLabel class="xl:hidden" path="BWF/shop/shared/edit" />

            <SvgIcon class="h-4 w-4" name="help-circle-outline" />
          </button>
        </slot>
      </div>
      <ModalComponent v-if="canEdit" :controller="addressModal">
        <template #headline>
          <p v-if="showTitle" class="block text-center text-lg md:text-2xl">
            <span> {{ addressTitle }}&nbsp;</span>
            <CommonLabel path="BWF/shop/shared/edit" />
          </p>
        </template>
        <AccountBillingAddressForm
          v-if="isBilling"
          :address="address as CustomerAddress"
          :shipping-edit-enabled="shippingEditEnabled"
          @close-modal="addressModal.close()"
        />
        <AccountShippingAddressForm
          v-else-if="isShipping"
          :address="address as CustomerAddress"
          @close-modal="addressModal.close()"
        />
        <AccountAddressForm
          v-else
          :address="address as CustomerAddress"
          @close-modal="addressModal.close()"
        />
      </ModalComponent>
    </template>
  </div>
</template>
